import { useState, useEffect, useMemo } from 'react';
import type { Question, Answer } from '@/types';

export const useConditionalQuestions = (
  allQuestions: Question[],
  answers: Map<string, Answer>
) => {
  const [activatedQuestions, setActivatedQuestions] = useState<Set<string>>(new Set());

  // Process which questions should be activated based on answers
  useEffect(() => {
    const activated = new Set<string>();

    // Go through all answers and collect activated questions
    answers.forEach((answer) => {
      const question = allQuestions.find(q => q.id === answer.questionId);
      if (question) {
        console.log(`📝 Answer for "${question.title}":`, answer.values);
        
        // Check each selected option
        answer.values.forEach(value => {
          const option = question.options.find(opt => opt.value === value);
          if (option) {
            console.log(`  ✓ Selected option "${option.label}" (value: ${option.value})`);
            if (option.activatedQuestions && option.activatedQuestions.length > 0) {
              console.log(`    → Activates:`, option.activatedQuestions);
              option.activatedQuestions.forEach(q => activated.add(q));
            }
          } else {
            console.log(`  ❌ Could not find option with value "${value}" in question`);
          }
        });
      }
    });

    console.log('🔍 Activated questions:', Array.from(activated));
    setActivatedQuestions(activated);
  }, [answers, allQuestions]);

  // Filter questions to only show those that should be displayed
  const visibleQuestions = useMemo(() => {
    console.log('📊 Filtering questions. Total:', allQuestions.length);
    console.log('✅ Activated:', Array.from(activatedQuestions));
    
    const filtered = allQuestions.filter(question => {
      // Questions with 'all' display condition are always shown
      if (question.displayCondition === 'all') {
        return true;
      }
      
      // Questions with 'condition' are only shown if activated
      if (question.displayCondition === 'condition') {
        // Check if this question's title matches any activated question
        const isActivated = Array.from(activatedQuestions).some(activatedTitle => 
          question.title === activatedTitle
        );
        console.log(`❓ Question "${question.title}" - Condition: ${question.displayCondition} - Activated: ${isActivated}`);
        return isActivated;
      }
      
      return false;
    });
    
    console.log(`📋 Visible questions: ${filtered.length} / ${allQuestions.length}`);
    return filtered;
  }, [allQuestions, activatedQuestions]);

  // Get the next available question index
  const getNextQuestionIndex = (currentIndex: number): number => {
    for (let i = currentIndex + 1; i < visibleQuestions.length; i++) {
      return i;
    }
    return currentIndex; // No next question available
  };

  // Get the previous available question index
  const getPreviousQuestionIndex = (currentIndex: number): number => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      return i;
    }
    return currentIndex; // No previous question available
  };

  return {
    visibleQuestions,
    activatedQuestions,
    getNextQuestionIndex,
    getPreviousQuestionIndex,
  };
};
